import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AttivaAccount = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [state, setState] = useState('checking'); // checking | invalid | form | done
  const [invite, setInvite] = useState(null);
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    axios.get(`${API}/auth/invite/${encodeURIComponent(token)}`)
      .then((res) => { setInvite(res.data); setState('form'); })
      .catch(() => setState('invalid'));
  }, [token]);

  const submit = async () => {
    if (pwd.length < 8) { toast.error('La password deve avere almeno 8 caratteri'); return; }
    if (pwd !== pwd2) { toast.error('Le due password non coincidono'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/accept-invite`, { token, password: pwd });
      setState('done');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore. Il link potrebbe essere scaduto.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md border-slate-200">
        <CardHeader className="text-center">
          <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Attiva il tuo account</CardTitle>
          <CardDescription>CRM JV Service</CardDescription>
        </CardHeader>
        <CardContent>
          {state === 'checking' && (
            <div className="flex items-center justify-center gap-2 text-slate-500 py-8">
              <Loader2 className="w-5 h-5 animate-spin" /> Verifica dell'invito…
            </div>
          )}

          {state === 'invalid' && (
            <div className="text-center py-6 space-y-3">
              <XCircle className="w-10 h-10 text-red-500 mx-auto" />
              <p className="text-slate-700">Invito non valido o scaduto.</p>
              <p className="text-sm text-slate-500">Chiedi a un amministratore di inviartene uno nuovo.</p>
              <Button variant="outline" onClick={() => navigate('/login')}>Vai al login</Button>
            </div>
          )}

          {state === 'form' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Ciao <strong>{invite?.full_name || invite?.email}</strong>, imposta la tua password
                per <strong>{invite?.email}</strong>.
              </p>
              <div className="space-y-2">
                <Label>Nuova password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="password" className="pl-9" value={pwd} minLength={8}
                    onChange={(e) => setPwd(e.target.value)} placeholder="almeno 8 caratteri" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Conferma password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="password" className="pl-9" value={pwd2}
                    onChange={(e) => setPwd2(e.target.value)} />
                </div>
              </div>
              <Button className="w-full" onClick={submit} disabled={saving}>
                {saving ? 'Attivazione…' : 'Attiva account'}
              </Button>
              <p className="text-xs text-slate-400 text-center">
                Al primo accesso ti verrà chiesto di configurare Google Authenticator (2FA).
              </p>
            </div>
          )}

          {state === 'done' && (
            <div className="text-center py-6 space-y-3">
              <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto" />
              <p className="text-slate-700">Account attivato.</p>
              <p className="text-sm text-slate-500">Ora accedi con la tua email e la password appena impostata.</p>
              <Button onClick={() => navigate('/login')}>Vai al login</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AttivaAccount;
